import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { clamp } from 'lodash';
import * as HSVHelpers from '../../helpers/hsv-helpers';

@Component({
  selector: 'mode-saturation-value',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './saturation-value.component.html',
  styleUrls: ['./saturation-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SaturationValueComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() hue = 0;
  @Input() saturation = 0;
  @Input() value = 0;
  @Input() height = 200;
  @Input() width = 200;

  @Output() selectedColor = new EventEmitter<{ s: number; v: number }>();
  @Output() moveStart = new EventEmitter();
  @Output() moveEnd = new EventEmitter();

  @ViewChild('container') container: ElementRef | undefined;
  @ViewChild('cursor') cursor: ElementRef | undefined;
  @ViewChild('gradient') gradientCanvas: ElementRef | undefined;

  constructor(private renderer: Renderer2) {
    this.removeKeyDownListenerFn = this.renderer.listen('document', 'keydown', this.onKeyDown.bind(this));
  }

  fullColorHue = '#FF0000';
  satPos = 100;
  valuePos = 100;
  useCSSGradient = false;
  lastColorEmitted = '';

  private containerBoundingBox: DOMRect | undefined;
  private removeCursorMoveListenerFn: (() => void) | undefined;
  private removeCursorUpListenerFn: (() => void) | undefined;
  private removeKeyDownListenerFn: () => void;
  private removeKeyUpListenerFn: (() => void) | undefined;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['saturation']) {
      this.satPos = this.saturation * this.width;
    }
    if (changes['value']) {
      this.valuePos = (1 - this.value) * this.height;
    }
    if (changes['hue']) {
      this.hue = clamp(changes['hue'].currentValue, 0, 360);
      this.fullColorHue = HSVHelpers.hsvAsHex(this.hue, 1, 1);

      this.drawGradientBackground();
    }

    if (
      (changes['saturation'] && !changes['saturation'].firstChange) ||
      (changes['value'] && !changes['value'].firstChange) ||
      (changes['hue'] && !changes['hue'].firstChange)
    ) {
      this.emitSelectedColor();
    }
  }
  ngOnDestroy(): void {
    this.removeListeners();
    this.removeKeyDownListenerFn();
  }

  ngAfterViewInit(): void {
    this.drawGradientBackground();
  }

  emitSelectedColor() {
    const currentSaturation = clamp(this.satPos / this.width, 0, 1);
    const currentValue = clamp(1 - this.valuePos / this.height, 0, 1);
    const color = HSVHelpers.hsvAsHex(this.hue, currentSaturation, currentValue);

    if (color !== this.lastColorEmitted) {
      this.lastColorEmitted = color;
      this.selectedColor.emit({ s: currentSaturation, v: currentValue });
    }
  }

  setColor(event: MouseEvent) {
    this.moveStart.emit();
    // Move the cursor under the click position.
    this.satPos = event.offsetX;
    this.valuePos = event.offsetY;
    this.emitSelectedColor();
    // Now handle like a cursor move.
    this.onCursorMouseDown(event);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.target === this.container?.nativeElement) {
      switch (event.key) {
        case 'ArrowDown':
          this.valuePos = Math.min((this.valuePos += 1), this.height);
          break;
        case 'ArrowUp':
          this.valuePos = Math.max((this.valuePos -= 1), 0);
          break;
        case 'ArrowRight':
          this.satPos = Math.min((this.satPos += 1), this.width);
          break;
        case 'ArrowLeft':
          this.satPos = Math.max((this.satPos -= 1), 0);
          break;
        case 'End':
          this.satPos = this.width;
          break;
        case 'Home':
          this.satPos = 0;
          break;
        case 'PageUp':
          this.valuePos = 0;
          break;
        case 'PageDown':
          this.valuePos = this.height;
          break;
        default:
          return;
      }
      if (this.removeKeyUpListenerFn == null) {
        this.removeKeyUpListenerFn = this.renderer.listen('document', 'keyup', this.onKeyUp.bind(this));
        this.moveStart.emit();
      }
      this.emitSelectedColor();
    }
  }

  onKeyUp() {
    this.moveEnd.emit();
    if (this.removeKeyUpListenerFn != null) {
      this.removeKeyUpListenerFn();
      this.removeKeyUpListenerFn = undefined;
    }
  }

  onCursorMouseDown(event: MouseEvent) {
    event.stopPropagation();
    this.removeListeners();
    this.moveStart.emit();
    this.containerBoundingBox = this.container?.nativeElement.getBoundingClientRect();
    if (this.container) {
      this.removeCursorMoveListenerFn = this.renderer.listen(
        'document',
        'mousemove',
        this.onCursorMouseMove.bind(this)
      );
      this.removeCursorUpListenerFn = this.renderer.listen('document', 'mouseup', this.onCursorMouseUp.bind(this));
    }
  }

  onCursorMouseMove(event: MouseEvent) {
    if (this.containerBoundingBox === undefined) {
      return;
    }
    // Position is calculated as the offset for the origin of the canvas.
    if (this.cursor) {
      this.satPos = clamp(event.clientX - this.containerBoundingBox.left, 0, this.containerBoundingBox.width);
      this.valuePos = clamp(event.clientY - this.containerBoundingBox.top, 0, this.containerBoundingBox.height);
      this.cursor.nativeElement.style.left = this.satPos + 'px';
      this.cursor.nativeElement.style.top = this.valuePos + 'px';
    }
    this.emitSelectedColor();
  }

  onCursorMouseUp() {
    this.removeListeners();
    this.emitSelectedColor();
    this.moveEnd.emit();
  }

  removeListeners() {
    if (this.removeCursorMoveListenerFn !== undefined) {
      this.removeCursorMoveListenerFn();
      this.removeCursorMoveListenerFn = undefined;
    }
    if (this.removeCursorUpListenerFn !== undefined) {
      this.removeCursorUpListenerFn();
      this.removeCursorUpListenerFn = undefined;
    }
  }

  /**
   * There are lots of examples that overlay two partial transparent
   * linear gradients. This 'approximates' the HSV color space but it
   * is not exact. When using a color picker on the center of the cursor,
   * the color did not match the color we were reporting. The difference
   * was mostly imperceivable but could be considered a bug if someone
   * really checks closely.
   *
   * If canvas2D is available, we will instead generate a bitmap image
   * using the exact pixel color generated from the HSV values. This
   * guarantees an exact match.
   *
   * If canvas2D is not available, we fall back to the linear gradient
   * method.
   */
  drawGradientBackground() {
    // Check if canvas2d is available in this context
    let ctx: any;
    try {
      ctx = this.gradientCanvas?.nativeElement?.getContext('2d');
      this.useCSSGradient = false;
    } catch (e) {
      this.useCSSGradient = true;
    }

    if (ctx != null && !this.useCSSGradient) {
      const imageData = ctx.createImageData(this.width, this.height);

      // Iterate through every pixel. Each pixel is stored as
      // four values (r, g, b, a).
      for (let i = 0; i < imageData.data.length; i += 4) {
        const colorIndex = i / 4;
        // Saturation is based on the pixel index horizontally across the width
        const satIndex = colorIndex % this.width;
        // Value is the pixel index vertically along the height
        const valueIndex = Math.floor(i / (this.width * 4));

        const rgbColor = HSVHelpers.getRGB(this.hue, satIndex / this.width, 1 - valueIndex / this.height);
        // Modify pixel data
        imageData.data[i + 0] = rgbColor.r; // R value
        imageData.data[i + 1] = rgbColor.g; // G value
        imageData.data[i + 2] = rgbColor.b; // B value
        imageData.data[i + 3] = 255; // A value
      }

      // Draw image data to the canvas
      ctx.putImageData(imageData, 0, 0);
    }
  }
}
